import { create } from 'zustand';
import { config } from '../config';
import { AppNode, isGroupNode, isNodeInGroup } from '../types';
import { useNodeStore } from './nodeStore';
import { useGroupStore } from './groupStore';
import { CoordinateUtils, BoundingBox } from '../utils/coordinateUtils';
import { SpatialIndex } from '../utils/spatialIndex';
import { performanceMonitor } from '../utils/performanceMonitor';

const spatialIndex = new SpatialIndex();

interface CanvasState {
  x: number;
  y: number;
  zoom: number;
  edgeDeselectCounter: number;
  setViewport: (x: number, y: number, zoom: number) => void;
  resetViewport: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  deselectEdges: () => void;

  checkNodeEnterGroup: (nodeId: string) => void;
  checkNodeExitGroup: (nodeId: string) => void;
  rebuildSpatialIndex: () => void;
  findNodesInBounds: (bounds: BoundingBox) => string[];
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  x: 0,
  y: 0,
  zoom: config.canvas.defaultZoom,
  edgeDeselectCounter: 0,

  setViewport: (x, y, zoom) => set({
    x,
    y,
    zoom: Math.max(config.canvas.minZoom, Math.min(config.canvas.maxZoom, zoom))
  }),

  resetViewport: () => set({ x: 0, y: 0, zoom: config.canvas.defaultZoom }),

  zoomIn: () => {
    const { zoom } = get();
    const newZoom = Math.min(config.canvas.maxZoom, zoom * 1.2);
    set({ zoom: newZoom });
  },

  zoomOut: () => {
    const { zoom } = get();
    const newZoom = Math.max(config.canvas.minZoom, zoom / 1.2);
    set({ zoom: newZoom });
  },

  deselectEdges: () => set((s) => ({ edgeDeselectCounter: s.edgeDeselectCounter + 1 })),

  checkNodeEnterGroup: (nodeId: string) => {
    const nodeState = useNodeStore.getState();
    const nodes = nodeState.nodes;
    const nodeMap = nodeState.nodeMap;
    const node = nodeMap.get(nodeId);
    if (!node || isGroupNode(node) || isNodeInGroup(node)) return;

    const nodeCenter = CoordinateUtils.getNodeCenter(node);

    const groupIds = useGroupStore.getState().getAllGroupIds();
    for (const gid of groupIds) {
      const group = nodeMap.get(gid);
      if (!group || !isGroupNode(group)) continue;

      const groupConfig = useGroupStore.getState().getGroupById(gid);
      if (groupConfig?.childIds.includes(nodeId)) continue;

      const bounds: BoundingBox = {
        x: group.x,
        y: group.y,
        width: group.width || 400,
        height: group.height || 300,
      };

      if (CoordinateUtils.isPointInBounds(nodeCenter, bounds)) {
        useNodeStore.getState()._batchUpdateNodes([
          {
            id: nodeId,
            changes: {
              parentNode: gid,
              groupId: gid,
            },
          },
        ]);

        useGroupStore.getState()._addChildToGroup(gid, nodeId);
        break;
      }
    }
  },

  checkNodeExitGroup: (nodeId: string) => {
    const nodeState = useNodeStore.getState();
    const nodeMap = nodeState.nodeMap;
    const node = nodeMap.get(nodeId);
    if (!node || !node.groupId) return;

    const groupId = node.groupId;
    const group = nodeMap.get(groupId);
    if (!group) return;

    const bounds: BoundingBox = {
      x: group.x,
      y: group.y,
      width: group.width || 400,
      height: group.height || 300,
    };

    const nodeCenter = CoordinateUtils.getNodeCenter(node);

    if (!CoordinateUtils.isPointInBounds(nodeCenter, bounds, 30)) {
      useNodeStore.getState()._batchUpdateNodes([
        {
          id: nodeId,
          changes: {
            parentNode: undefined,
            extent: undefined,
            expandParent: undefined,
            groupId: undefined,
          },
        },
      ]);

      useGroupStore.getState()._removeChildFromGroup(groupId, nodeId);
    }
  },

  rebuildSpatialIndex: () => {
    performanceMonitor.start('rebuildSpatialIndex');
    const nodes = useNodeStore.getState().nodes;
    spatialIndex.clear();
    spatialIndex.build(
      nodes
        .filter(n => !isGroupNode(n))
        .map(n => ({
          id: n.id,
          bounds: CoordinateUtils.getNodeBounds(n),
        }))
    );
    performanceMonitor.end('rebuildSpatialIndex');
  },

  findNodesInBounds: (bounds: BoundingBox) => {
    const results = spatialIndex.query(bounds);
    return results.map(r => r.id);
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}
